import { TableRow } from "@material-ui/core"
import {
  Backdrop,
  Box,
  Menu,
  MenuItem,
  styled,
  TableCell,
  Typography,
  useTheme
} from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import styles from "./ContestTable.module.scss"
import { useHistory } from "react-router-dom"
import { useDispatch } from "react-redux"
import { getFormattedDate } from "helpers/functions"
import { marketingActions } from "redux/marketing/slice/user"
import { editWeeklyChallengeAction } from "redux/marketing/actions"
import { swapWeeklyChallengesAction } from "redux/marketing/actions/user"
import AppSelect from "components/StyledComponents/AppSelect"
import CommonModal from "components/CommonModal"
import AppButton from "components/StyledComponents/AppButton"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))

const ContestRow = ({
  row,
  columns,
  enableRepeatActions = false,
  challengeOptions = [],
  onRepeatSuccess
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const history = useHistory()
  const [anchorEl, setAnchorEl] = useState(false)
  const openMenu = Boolean(anchorEl)
  const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false)
  const [selectedChallengeId, setSelectedChallengeId] = useState("")
  const [repeatError, setRepeatError] = useState("")
  const [isRepeating, setIsRepeating] = useState(false)

  const formattedDate = getFormattedDate(row.creation_date)

  const getStatusStyles = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return { color: theme.palette.green.main }
      case "draft":
        return { color: theme.palette.orange.main }
      case "published":
        return { color: theme.palette.green.light }
      default:
        return { color: theme.palette.text.primary }
    }
  }

  const onOptionsClick = (e) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    dispatch(marketingActions.setWeeklyChallengeId(row.id))
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const editContest = () => {
    dispatch(marketingActions.setWeeklyChallengeId(row.id))
    history.push("/learning_development/dashboard/weekly-challenge/edit")
  }

  const viewContest = () => {
    dispatch(marketingActions.setWeeklyChallengeId(row.id))
    history.push("/learning_development/dashboard/weekly-challenge/view-weekly-challenge")
  }

  const handleRoute = () => {
    history.push("/learning_development/dashboard/weekly-challenge")
  }

  const handleDraft = (item) => {
    const payload = {
      flashcard_coins: item.flash_cards_coins,
      status: "Draft"
    }

    dispatch(
      editWeeklyChallengeAction({
        payload,
        handleRoute,
        isPublished: true
      })
    )
  }

  const checkStatus = () => {
    if (row?.status?.toLowerCase() === "published" || row?.status?.toLowerCase() === "completed") {
      return new Date(row?.start_date) > new Date()
    }
    return true
  }

  const repeatableChallenges = useMemo(() => {
    if (!enableRepeatActions) return []
    return (
      challengeOptions
        ?.filter((challenge) => challenge?.id !== row?.id)
        ?.map((challenge) => ({
          label: challenge?.name ?? "-",
          value: challenge?.id
        })) ?? []
    )
  }, [row?.id, challengeOptions, enableRepeatActions])

  const handleRepeatModalOpen = () => {
    handleMenuClose()
    setRepeatError("")
    setIsRepeatModalOpen(true)
  }

  const handleRepeatModalClose = () => {
    setIsRepeatModalOpen(false)
    setSelectedChallengeId("")
    setRepeatError("")
    setIsRepeating(false)
  }

  const handleRepeatSubmit = () => {
    if (!selectedChallengeId) {
      setRepeatError("Please select a challenge to repeat")
      return
    }
    setRepeatError("")
    setIsRepeating(true)
    dispatch(
      swapWeeklyChallengesAction({
        payload: {
          challenge_to_replace_with: selectedChallengeId,
          challenge_to_be_replaced: row?.id
        },
        onSuccess: () => {
          setIsRepeating(false)
          handleRepeatModalClose()
          if (typeof onRepeatSuccess === "function") {
            onRepeatSuccess()
          }
        }
      })
    )
  }

  const repeatModalActions = (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pb: 2 }}>
      <AppButton onClick={handleRepeatModalClose} disabled={isRepeating}>
        Cancel
      </AppButton>
      <AppButton onClick={handleRepeatSubmit} disabled={!selectedChallengeId || isRepeating}>
        {isRepeating ? "Swapping..." : "Swap Challenge"}
      </AppButton>
    </Box>
  )

  const canRepeatCurrentRow =
    enableRepeatActions &&
    row?.status?.toLowerCase() === "draft" &&
    repeatableChallenges?.length > 0

  useEffect(() => {
    dispatch(marketingActions.setWeeklyChallengeId(""))
    dispatch(marketingActions.setWeeklyChallengeData({}))
  }, [])

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: columns?.[0]?.maxWidth }}>
          <Typography align={columns?.[0]?.align} variant="body1Regular">
            {row.name}
          </Typography>
        </TableCell>
        <TableCell align={columns?.[2]?.align} sx={{ width: columns?.[1]?.maxWidth }}>
          <Typography variant="body1Regular">{`Every ${
            row.published_week_day.toLowerCase() ?? ""
          }`}</Typography>
        </TableCell>
        <TableCell align={columns?.[3]?.align} sx={{ width: columns?.[2]?.maxWidth }}>
          <Typography variant="body1Regular">{formattedDate}</Typography>
        </TableCell>
        <TableCell align={columns?.[4]?.align} sx={{ width: columns?.[3]?.maxWidth }}>
          <Typography sx={() => getStatusStyles(row.status)} variant="body1Regular">
            {row.status}
          </Typography>
        </TableCell>
        <TableCell
          align="center"
          onClick={(e) => e.stopPropagation()}
          sx={{ width: columns?.[4]?.maxWidth }}>
          <span onClick={onOptionsClick} className={styles.actionBtnCtr}>
            <MoreHorizIcon
              sx={() => ({
                height: "24px",
                width: "24px"
              })}
            />
          </span>
          <Backdrop sx={{ color: "#fff", zIndex: 1300 }} open={openMenu} onClick={handleMenuClose}>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  marginTop: "15px",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "12px",
                  "& .MuiList-root": {
                    backgroundColor: theme.palette.background.default,
                    width: "225px",
                    boxSizing: "border-box",
                    padding: "10px 0"
                  }
                }
              }}>
              <StyledMenuItem onClick={viewContest}>
                <Typography variant="body1Regular">View</Typography>
              </StyledMenuItem>
              {checkStatus() && (
                <StyledMenuItem onClick={editContest}>
                  <Typography variant="body1Regular">Edit</Typography>
                </StyledMenuItem>
              )}
              {row?.status?.toLowerCase() === "published" && (
                <StyledMenuItem onClick={() => handleDraft(row)}>
                  <Typography variant="body1Regular">Make Draft</Typography>
                </StyledMenuItem>
              )}
              {canRepeatCurrentRow ? (
                <StyledMenuItem onClick={handleRepeatModalOpen}>
                  <Typography variant="body1Regular">Swap Challenge</Typography>
                </StyledMenuItem>
              ) : null}
            </Menu>
          </Backdrop>
        </TableCell>
      </TableRow>
      <CommonModal
        open={isRepeatModalOpen}
        onClose={handleRepeatModalClose}
        maxWidth="sm"
        actionButtons={repeatModalActions}>
        <Box sx={{ pt: 2 }}>
          <Box className="d-flex align-items-center justify-content-center flex-column gap-1">
            <Typography variant="h3" sx={{ mb: 1 }}>
              Swap Challenge
            </Typography>
            <Typography variant="body2Regular">
              Select an existing challenge to swap for this draft slot.
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <AppSelect
              label="Challenges"
              id={`repeat-challenge-${row?.id}`}
              value={selectedChallengeId}
              onChange={(event) => {
                setSelectedChallengeId(event.target.value)
                setRepeatError("")
              }}
              menuItems={repeatableChallenges}
              isRequired
              error={repeatError}
            />
          </Box>
        </Box>
      </CommonModal>
    </>
  )
}

export default ContestRow
